import React, {useRef} from 'react';
import {ScrollView, View} from 'react-native';
import {Image, ThemeProvider} from '@rneui/themed';
import _ from 'lodash';
import {observer} from 'mobx-react';

import DepartmentCarousel from '../../components/DepartmentCarousel';
import {MemberPriceCarousel} from '../../components/ProductCarousel';
import PromotionsCarousel from '../../components/PromotionsCarousel';
import ShopLoyaltyEnabled from '../../components/rewards/ShopLoyaltyEnabled';
import TitleLine from '../../components/rewards/TitleLine';
import ShopByBrand from '../../components/ShopByBrand';
import ContentView from '../../components/ui-controls/layout/ContentView';
import WebFooter from '../../components/WebFooter';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {
  AirtableBrand,
  AirtablePromotionItem,
} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import { HomepageStore } from '../../stores/homepageStore';
import { useStores } from '../../stores/use-stores';
import rewardsTheme from '../../theme/rewardsTheme';
import {brandRewardsCopy} from '../../utils/rewards';

import Banner from './Banner';
import { formatHeroImage } from './shared';

const HomepageScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.HOME>) => {
    const {isWebDesktopScreen} = useWebScreenDimensions();
    const {shopSelectionStore} = useStores();
  
    const homepageStoreRef = useRef(new HomepageStore(shopSelectionStore));
    const navigateToRewards = () => navigation.navigate(AppRoute.REWARDS);
    const navigateToDiscover = () => navigation.navigate(AppRoute.DISCOVER);

    return (
      <ThemeProvider theme={rewardsTheme}>
        <ContentView testID="homepage-screen">
          <ScrollView
            contentContainerStyle={{gap: isWebDesktopScreen ? 40 : 24}}
            showsVerticalScrollIndicator={isWebDesktopScreen}>
            
            <Banner bannerItem={homepageStoreRef.current.data?.AlertBanner} />
            <Banner bannerItem={homepageStoreRef.current.data?.TopBanner} />
            
            <ShopLoyaltyEnabled>
              <View>
                <TitleLine
                  title={_.toUpper(brandRewardsCopy.loyaltyProgramName)}
                  onSeeAllPress={navigateToRewards}
                />
                <Image
                  onPress={navigateToRewards}
                  source={formatHeroImage(
                    homepageStoreRef.current.data?.LoyaltyAd,
                    isWebDesktopScreen,
                  )}
                  containerStyle={{
                    width: '100%',
                    aspectRatio: 1.54,
                    flex: 1,
                    flexGrow: 1,
                    borderRadius: 10,
                    ...(isWebDesktopScreen ? {maxHeight: 400} : {}),
                  }}
                />
              </View>
              <MemberPriceCarousel
                title={_.toUpper(brandRewardsCopy.memberPublicPricingTitle)}
                member_price="public"
                onSeeAllPress={() => {
                  navigation.navigate(AppRoute.MEMBERS_PRICE_LIST, {
                    external_sell_type: 'members-price',
                  });
                }}
              />
            </ShopLoyaltyEnabled>

            <PromotionsCarousel
              onPromotionPress={(promotion: AirtablePromotionItem) => {
                navigation.navigate(AppRoute.PROMOTION, {
                  promotion,
                });
              }}
              onSeeAllPress={() => {
                navigation.navigate(AppRoute.PROMOTIONS_CURRENT);
              }}
            />

            <DepartmentCarousel
              departmentName="whats-new"
              onSeeAllPress={department => {
                navigation.navigate(AppRoute.DEPARTMENT, {
                  department,
                });
              }}
            />

            <View>
              <TitleLine
                title="DISCOVER"
                onSeeAllPress={navigateToDiscover}
              />
              <Image
                onPress={navigateToDiscover}
                source={formatHeroImage(
                  homepageStoreRef.current.data?.DiscoverFeature,
                  isWebDesktopScreen,
                )}
                containerStyle={{
                  width: '100%',
                  aspectRatio: 1.54,
                  flex: 1,
                  flexGrow: 1,
                  borderRadius: 10,
                  ...(isWebDesktopScreen ? {maxHeight: 400} : {}),
                }}
              />
            </View>

            <ShopByBrand
              isLoading={homepageStoreRef.current.isLoading}
              brands={homepageStoreRef.current.data?.HeroBrands?.brands || []}
              onSeeAllPress={() => {
                navigation.navigate(AppRoute.FAVOURITE_BRANDS);
              }}
              onBrandPress={(brand: AirtableBrand) => {
                navigation.navigate(AppRoute.BRAND, {
                  brand,
                });
              }}
            />


            {!!isWebDesktopScreen && <WebFooter />}
          </ScrollView>
        </ContentView>
      </ThemeProvider>
    );
  },
);

export default HomepageScreen;
